import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  MenuItem,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import timezonesData from "../../utils/data/timezones.json";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { CCMPatient } from "../../interfaces/CCMPatient";

interface EditPatientDialogProps {
  open: boolean;
  patient: CCMPatient | null;
  onClose: () => void;
  onSave: (updatedPatient: Partial<CCMPatient>) => void;
  isLoading?: boolean;
  error?: string;
}

interface TimezoneOption {
  offset: string; // e.g. "UTC +2"
  label: string; // e.g. "UTC +2 (Oslo, Stockholm, Cairo, Helsinki)"
}

// Helper function to parse a timezone offset string into a number.
const parseUTCOffset = (offset: string): number => {
  const cleaned = offset.replace("UTC", "").replace(/\s/g, "");
  const parts = cleaned.split(":");
  const hours = parseFloat(parts[0]);
  const minutes = parts[1] ? parseFloat(parts[1]) : 0;
  return hours + minutes / 60;
};

// Flatten the JSON data into an array of options.
const flattenTimezones = (): TimezoneOption[] => {
  const options: TimezoneOption[] = [];
  for (const [offset, cities] of Object.entries(timezonesData)) {
    const label = `${offset} (${cities.join(", ")})`;
    options.push({ offset, label });
  }
  // Sort by UTC offset (numerically).
  return options.sort(
    (a, b) => parseUTCOffset(a.offset) - parseUTCOffset(b.offset)
  );
};

// Get the browser's timezone in the format used by the timezone data.
const getBrowserTimezone = (): string => {
  const offset = new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset <= 0 ? "+" : "-";
  return `UTC ${sign}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : ""}`;
};

// Helper function to parse a date string and return a valid Date object or null.
const getValidDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  // Parse YYYY-MM-DD format manually to avoid timezone issues
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Fallback to standard parsing
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

const timezoneOptions = flattenTimezones();

const EditPatientDialog: React.FC<EditPatientDialogProps> = ({
  open,
  patient,
  onClose,
  onSave,
  isLoading = false,
  error = "",
}) => {
  const { t } = useTranslation();

  // Form state
  const [patientId, setPatientId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [emailId, setEmailId] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("M");
  const [timezone, setTimezone] = useState("");

  // Date picker state
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Initialize form with patient data when dialog opens
  useEffect(() => {
    if (patient && open) {
      setPatientId(patient.patient_id || "");
      setFirstName(patient.first_name || "");
      setLastName(patient.last_name || "");
      setPhone(patient.phone || "");
      setEmailId(patient.email || "");
      // dob and gender may not exist in the API data, so use empty defaults
      setDob(patient.dob || "");
      setGender(patient.gender || "M");
      setTimezone(patient.timezone || getBrowserTimezone());
      setSelectedDate(patient.dob ? getValidDate(patient.dob) : null);
    }
  }, [patient, open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setPatientId("");
      setFirstName("");
      setLastName("");
      setPhone("");
      setEmailId("");
      setDob("");
      setGender("M");
      setTimezone("");
      setSelectedDate(null);
    }
  }, [open]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    switch (name) {
      case "patientId":
        setPatientId(value);
        break;
      case "firstName":
        setFirstName(value);
        break;
      case "lastName":
        setLastName(value);
        break;
      case "phone":
        setPhone(value);
        break;
      case "emailId":
        setEmailId(value);
        break;
      case "dob":
        setDob(value);
        break;
      case "gender":
        setGender(value);
        break;
      default:
        break;
    }
  };

  const handleSave = () => {
    const updatedPatient: Partial<CCMPatient> = {
      patient_id: patientId,
      first_name: firstName,
      last_name: lastName,
      phone: phone,
      email: emailId,
      timezone: timezone,
    };
    
    // Only include dob and gender if they have values
    if (dob) {
      updatedPatient.dob = dob;
    }
    if (gender) {
      updatedPatient.gender = gender;
    }
    
    onSave(updatedPatient);
  };

  // Use the provided timezone if available; otherwise, fall back to the browser's timezone.
  const effectiveTimezone = timezone || getBrowserTimezone();
  const effectiveOffset = parseUTCOffset(effectiveTimezone);

  let currentOption =
    timezoneOptions.find(
      (option) =>
        Math.abs(parseUTCOffset(option.offset) - effectiveOffset) < 0.001
    ) || null;

  if (!currentOption) {
    const browserTz = getBrowserTimezone();
    const browserOffset = parseUTCOffset(browserTz);
    currentOption =
      timezoneOptions.find(
        (option) =>
          Math.abs(parseUTCOffset(option.offset) - browserOffset) < 0.001
      ) || null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("Edit Patient Information")}</DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        <TextField
          margin="normal"
          label={t("Patient ID")}
          name="patientId"
          value={patientId}
          onChange={handleInputChange}
          fullWidth
          disabled // Patient ID should not be editable
        />
        <TextField
          margin="normal"
          label={t("First Name")}
          name="firstName"
          value={firstName}
          onChange={handleInputChange}
          fullWidth
        />
        <TextField
          margin="normal"
          label={t("Last Name")}
          name="lastName"
          value={lastName}
          onChange={handleInputChange}
          fullWidth
        />
        <TextField
          margin="normal"
          label={t("Phone")}
          name="phone"
          value={phone}
          onChange={handleInputChange}
          fullWidth
        />
        <TextField
          margin="normal"
          label={t("Email ID")}
          name="emailId"
          value={emailId}
          onChange={handleInputChange}
          fullWidth
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t("Date of Birth")}
            value={selectedDate}
            inputFormat="MM/dd/yyyy"
            mask="__/__/____"
            maxDate={new Date()}
            onError={(error, value) => {
              console.error("DatePicker error:", error);
            }}
            onChange={(newValue) => {
              setSelectedDate(newValue);
              if (newValue && !isNaN(newValue.getTime())) {
                // Format date in local timezone to avoid UTC conversion issues
                const year = newValue.getFullYear();
                const month = String(newValue.getMonth() + 1).padStart(2, '0');
                const day = String(newValue.getDate()).padStart(2, '0');
                const formattedDate = `${year}-${month}-${day}`;
                setDob(formattedDate);
              } else {
                setDob("");
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  readOnly: true,
                  autoComplete: "off",
                }}
              />
            )}
          />
        </LocalizationProvider>
        <TextField
          margin="normal"
          label={t("Gender")}
          name="gender"
          select
          value={gender}
          onChange={handleInputChange}
          fullWidth
        >
          <MenuItem value="M">{t("Male")}</MenuItem>
          <MenuItem value="F">{t("Female")}</MenuItem>
          <MenuItem value="O">{t("Other")}</MenuItem>
        </TextField>
        <Autocomplete
          options={timezoneOptions}
          getOptionLabel={(option) => option.label}
          value={currentOption}
          onChange={(event, newValue) => {
            const newTimezone = newValue ? newValue.offset : getBrowserTimezone();
            setTimezone(newTimezone);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="normal"
              label={t("Timezone")}
              fullWidth
            />
          )}
        />
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isLoading || !firstName || !lastName}
        >
          {isLoading ? t("Saving...") : t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPatientDialog;